import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, Globe } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { language, setLanguage, t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const isLargeScreen = window.innerWidth >= 1024;
      const offset = isLargeScreen ? 140 : 80; // height of sticky header
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const handleNavigation = (id) => {
    setIsMobileMenuOpen(false);
    if (location.pathname !== '/') {
      navigate('/', { state: { scrollTo: id } });
    } else {
      scrollToSection(id);
    }
  };

  const handleHomeClick = () => {
    setIsMobileMenuOpen(false);
    if (location.pathname !== '/') {
      navigate('/');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleContactClick = () => {
    setIsMobileMenuOpen(false);
    navigate('/kontakt');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleExportCarsClick = () => {
    setIsMobileMenuOpen(false);
    navigate('/export-cars');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleOurHistoryClick = () => {
    setIsMobileMenuOpen(false);
    navigate('/vores-historie');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSellLeasingCarClick = () => {
    setIsMobileMenuOpen(false);
    navigate('/saelg-din-bil');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-100 shadow-sm py-3 lg:py-4 transition-all duration-300"
    >
      <div className="max-w-7xl mx-auto px-6 flex flex-col gap-2 lg:gap-3">
        {/* Top Row: Logo & CTA / Hamburger */}
        <div className="flex items-center justify-between w-full">
          {/* Logo */}
          <button
            onClick={handleHomeClick}
            className="flex items-center text-left focus:outline-none cursor-pointer"
          >
            <img 
              src="https://assets.auxodev.dk/assets/5bfde40d-44ae-4b1e-9e89-bf8f3817c44f/ea839c94-dab5-4ca3-9c09-fd0ca187cd26.gif" 
              alt="LeaseRetur Logo" 
              className="h-16 md:h-20 w-auto object-contain transition-transform hover:scale-[1.02]"
            />
          </button>

          {/* Right side: Desktop CTA (lg and up) & Hamburger (below lg) */}
          <div className="flex items-center gap-6">
            {/* Desktop Language Selector */}
            <div className="hidden lg:flex items-center gap-2 border-r border-slate-200 pr-4 mr-2">
              <Globe className="h-4 w-4 text-slate-400" />
              <button
                onClick={() => setLanguage('da')}
                className={`text-xs font-bold px-1.5 py-1 transition-colors cursor-pointer ${
                  language === 'da' ? 'text-black border-b-2 border-[#76eeda]' : 'text-slate-400 hover:text-slate-900'
                }`}
              >
                DA
              </button>
              <button
                onClick={() => setLanguage('en')}
                className={`text-xs font-bold px-1.5 py-1 transition-colors cursor-pointer ${
                  language === 'en' ? 'text-black border-b-2 border-[#76eeda]' : 'text-slate-400 hover:text-slate-900'
                }`}
              >
                EN
              </button>
              <button
                onClick={() => setLanguage('de')}
                className={`text-xs font-bold px-1.5 py-1 transition-colors cursor-pointer ${
                  language === 'de' ? 'text-black border-b-2 border-[#76eeda]' : 'text-slate-400 hover:text-slate-900'
                }`}
              >
                DE
              </button>
            </div>

            <div className="hidden lg:flex items-center">
              <button
                onClick={handleSellLeasingCarClick}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-none bg-[#76eeda] hover:bg-[#5cdbc7] text-black text-sm font-bold shadow-sm hover:shadow transition-all hover:-translate-y-0.5 uppercase tracking-wider cursor-pointer"
              >
                {t('nav.cta')}
              </button>
            </div>

            {/* Mobile/Tablet Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 text-slate-600 focus:outline-none transition-colors cursor-pointer"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Bottom Row: Desktop Navigation (lg and up) - Left Aligned under Logo */}
        <nav className="hidden lg:flex items-center gap-8 pt-1">
          <button
            onClick={handleHomeClick}
            className="text-xs font-bold text-slate-600 hover:text-slate-900 transition-colors cursor-pointer uppercase tracking-wider"
          >
            {t('nav.home')}
          </button>
          <button
            onClick={handleSellLeasingCarClick}
            className="text-xs font-bold text-slate-600 hover:text-slate-900 transition-colors cursor-pointer uppercase tracking-wider"
          >
            {t('nav.sell')}
          </button>
          <button
            onClick={handleExportCarsClick}
            className="text-xs font-bold text-slate-600 hover:text-slate-900 transition-colors cursor-pointer uppercase tracking-wider"
          >
            {t('nav.export')}
          </button>
          <button
            onClick={handleOurHistoryClick}
            className="text-xs font-bold text-slate-600 hover:text-slate-900 transition-colors cursor-pointer uppercase tracking-wider"
          >
            {t('nav.history')}
          </button>
          <button
            onClick={handleContactClick}
            className="text-xs font-bold text-slate-600 hover:text-slate-900 transition-colors cursor-pointer uppercase tracking-wider"
          >
            {t('nav.contact')}
          </button>
        </nav>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden absolute top-full left-0 right-0 bg-white border-b border-slate-100 shadow-lg py-6 px-6 flex flex-col gap-4 transition-all duration-300 ease-in-out origin-top ${
          isMobileMenuOpen
            ? "opacity-100 scale-y-100 visible"
            : "opacity-0 scale-y-95 invisible pointer-events-none"
        }`}
      >
          {/* Mobile Language Selector */}
          <div className="flex items-center gap-4 py-2 border-b border-slate-100">
            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
              <Globe className="h-4 w-4" /> Sprog / Language:
            </span>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setLanguage('da')}
                className={`text-xs font-bold px-2 py-1 transition-colors cursor-pointer ${
                  language === 'da' ? 'text-black bg-slate-100' : 'text-slate-400'
                }`}
              >
                Dansk
              </button>
              <button
                onClick={() => setLanguage('en')}
                className={`text-xs font-bold px-2 py-1 transition-colors cursor-pointer ${
                  language === 'en' ? 'text-black bg-slate-100' : 'text-slate-400'
                }`}
              >
                English
              </button>
              <button
                onClick={() => setLanguage('de')}
                className={`text-xs font-bold px-2 py-1 transition-colors cursor-pointer ${
                  language === 'de' ? 'text-black bg-slate-100' : 'text-slate-400'
                }`}
              >
                Deutsch
              </button>
            </div>
          </div>

          <button
            onClick={handleHomeClick}
            className="text-left py-2 text-sm font-bold text-slate-700 hover:text-slate-900 uppercase tracking-wider cursor-pointer"
          >
            {t('nav.home')}
          </button>
          <button
            onClick={handleSellLeasingCarClick}
            className="text-left py-2 text-sm font-bold text-slate-700 hover:text-slate-900 uppercase tracking-wider cursor-pointer"
          >
            {t('nav.sell')}
          </button>
          <button
            onClick={handleExportCarsClick}
            className="text-left py-2 text-sm font-bold text-slate-700 hover:text-slate-900 uppercase tracking-wider cursor-pointer"
          >
            {t('nav.export')}
          </button>
          <button
            onClick={handleOurHistoryClick}
            className="text-left py-2 text-sm font-bold text-slate-700 hover:text-slate-900 uppercase tracking-wider cursor-pointer"
          >
            {t('nav.history')}
          </button>
          <button
            onClick={handleContactClick}
            className="text-left py-2 text-sm font-bold text-slate-700 hover:text-slate-900 uppercase tracking-wider cursor-pointer"
          >
            {t('nav.contact')}
          </button>
          <hr className="border-slate-100 my-1" />
          <button
            onClick={handleSellLeasingCarClick}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-none bg-[#76eeda] hover:bg-[#5cdbc7] text-black font-bold shadow-sm uppercase tracking-wider cursor-pointer"
          >
            {t('nav.cta')}
          </button>
        </div>
    </header>
  );
}
